import { Avatar, Button, Chip, ListItem, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { DRAWER_MAX_SELECTION_COUNT } from "app/appConstants";
import { AxiosResponse, GenericAbortSignal } from "axios";
import { MprcTypography } from "components";
import { IconButtonPreview } from "components/iconButtons/IconButtonPreview";
import { TrainingSelectorDto, TrainingSelectorDtoPaginatedList, TrainingType } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";
import { IContentHeaderProps } from "views/common/GenericListDrawer/GenericListDrawer";
import { LmsTrainingDefaultThumbnailUrl } from "views/lms/components/LmsTypeImage";
import { TrainingRegistrationDialog } from "views/lms/student/Registration/TrainingRegistrationDialog";

////////////////////////////////////////////////////////////////////////////////

type ItemData = GenericListItemsType<TrainingSelectorDto>;

export type TrainingSelectorFilterOptions = {
    searchText: string;
    pageNumber: number;
    pageSize: number;
    signal?: AbortSignal;
};

////////////////////////////////////////////////////////////////////////////////

export type TrainingSelectorProps = {
    title?: string;
    subtitle?: string;
    multi?: boolean;
    onClose: (trainings: TrainingSelectorDto[] | undefined) => void;
    contentHeader?: (data: IContentHeaderProps) => React.ReactNode;
    showPreview?: boolean;
    trainingType?: TrainingType;
};

type IProps = TrainingSelectorProps & {
    onGetData: (options: TrainingSelectorFilterOptions) => Promise<AxiosResponse<TrainingSelectorDtoPaginatedList>>;
};

/////////////////////////////////////////////////////////////////////////////////

export const BaseTrainingSelectorDrawer: React.FC<IProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation(["common", "lms"]);

    const { trainingType, showPreview, title, subtitle, contentHeader, multi = false, onGetData, onClose } = props;

    const [dialog, setDialog] = useState<any>();
    const [selected, setSelection] = useState<TrainingSelectorDto[]>([]);

    ////////////////////////////////////////////////////////////////////////////////

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<TrainingSelectorDtoPaginatedList> => {
        return new Promise((resolve, reject) => {
            onGetData({
                ...genericFilter,
                signal: signal as AbortSignal
            })
                .then((response) => resolve(response.data))
                .catch(reject);
        });
    };

    // Ao selecionar um item da lista.
    const onItemClick = (item: TrainingSelectorDto) => {
        if (!multi)
            onClose([item]);
        else {
            if (selected.find(p => p.trainingId == item.trainingId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("Selector.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT, ns: "shared" }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("Selector.AlreadySelectedMessage", { name: item.name, ns: "shared" }), { variant: "error" });
            }
        }
    };

    ///
    const onPreviewClick = (item: TrainingSelectorDto) => {
        setDialog(
            <TrainingRegistrationDialog
                trainingId={item.trainingId}
                readonly
                onClose={() => setDialog(null)}
            />
        );
    };

    /**
     * Renderiza uma linha da Lista.
     * @param props
     */
    function renderRow(props: ListChildComponentProps<ItemData>) {
        const { index, style } = props;
        const { items } = props.data;

        const item = items[index];
        if (item == undefined) return <></>;

        return (
            <div key={index} style={style}>
                <ListItem
                    disablePadding
                    disableGutters
                    dense
                    secondaryAction={showPreview &&
                        <IconButtonPreview color="muted" onClick={() => onPreviewClick(item)} size="small" />
                    }
                >
                    <ListItemButton disableGutters selected={item.selected} onClick={() => onItemClick(item)} sx={{ height: style.height }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                                variant="rounded"
                                src={LmsTrainingDefaultThumbnailUrl(item.contentType, item.thumbnailUrl)}
                                sx={(theme) => ({ width: 35, height: 24, backgroundColor: theme.palette.grey[200] })}
                            />
                        </ListItemAvatar>
                        <ListItemText title={item.name}>
                            <MprcTypography variant="h6" noWrapRows={2}>
                                {item.name}
                            </MprcTypography>
                        </ListItemText>
                    </ListItemButton>
                </ListItem>
            </div>
        );
    }

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const RenderTitle = (
        title ||
        (trainingType != undefined
            ? t(`Training.SelectDrawer.${TrainingType[trainingType].toString()}.Title`, { ns: "lms" })
            : t("Training.SelectDrawer.Title", { ns: "lms" }))
    );

    const emptyDataMessage =
        trainingType != undefined
            ? t(`Training.SelectDrawer.${TrainingType[trainingType].toString()}.ThereAreNoResults`, { ns: "lms" })
            : t("Training.SelectDrawer.ThereAreNoResults", { ns: "lms" });

    const emptyFilterMessage =
        trainingType != undefined
            ? t(`Training.SelectDrawer.${TrainingType[trainingType].toString()}.NoRecordsFoundText`, { ns: "lms" })
            : t("Training.SelectDrawer.NoRecordsFoundText", { ns: "lms" });

    return (
        <GenericListDrawer
            title={RenderTitle}
            subtitle={subtitle}
            itemHeight={40}
            onClose={() => onClose(undefined)}
            onGetData={handleGetData}
            onViewerClick={onItemClick}
            emptyDataMessage={emptyDataMessage}
            emptyFilterMessage={emptyFilterMessage}
            contentHeader={contentHeader}
            renderRow={renderRow}
            selection="history"
            footer={
                <>

                    {dialog}

                    <div style={{ width: "100%" }}>
                        {
                            multi && selected.length > 0 &&
                            <>
                                <div style={{ maxHeight: 200, overflow: "auto" }}>
                                    <p><small>{t("Selector.SelectedText", { ns: "shared" })}:</small></p>
                                    {
                                        selected.map((item, index) => (
                                            <Chip
                                                key={index}
                                                label={item.name}
                                                size="small"
                                                sx={{ mb: 0.5, mr: 0.5 }}
                                                onDelete={() =>
                                                    setSelection(selected.filter(p => p.trainingId != item.trainingId))
                                                }
                                            />
                                        ))
                                    }
                                </div>
                                <hr />
                                <div style={{ textAlign: "center" }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => onClose(selected)}
                                    >
                                        {t("Selector.AddSelectedButtonText", { ns: "shared" })}
                                    </Button >
                                </div>
                            </>
                        }
                    </div>
                </>
            }
        />
    );
};
